<template>
<div></div>
</template>

<script>
import vFormAndProjectMixin from "@/components/vForm/mixins/vFormAndProjectMixin.js.vue";
import {vFormButtonActionModes, vFormControls, vformTargetTypes} from "@/enum";
import ElementMethodMixinJs from "@/components/vForm/mixins/ElementMethodMixin.js.vue";
export default {
  name: "FormTreeTraverserMixin.js",
  mixins: [vFormAndProjectMixin, ElementMethodMixinJs],
  data() {
    return {
      targetStack: [], // lifo
      visitedStack: [],
      connections: [],
      vFormControls
    };
  },
  methods: {
    traverseTree(vform, slides, addConnectionFunction = null, addSlideConnectionFunction = null) {
      // in case this method is called multiple times, reset everything
      this.targetStack = [];
      this.visitedStack = [];
      this.connections = [];
      let missingSlides = this.checkMissingSlides(slides);
      let rootNode = slides[0].id;
      while(missingSlides.length) {
        this.traverseNode(vform, slides, vform.steps, rootNode, addConnectionFunction);
        missingSlides = this.checkMissingSlides(slides);
        if(missingSlides.length) {
          // take the first missing slide and set it as rootNode
          rootNode = missingSlides[0];
        }
      }
      if(addSlideConnectionFunction) {
        addSlideConnectionFunction(this.connections);
      }
      return this.connections;
    },
    traverseNode(vform, slides, steps, slideId, addConnectionFunction = null) {
      const step = this.getStepForSlide(slides, vform.steps, slideId, slideId);

      if(step) {
        this.handleConnectionStuffForStep(step, slideId, vform, slides, steps, addConnectionFunction);
      }
      this.handleConnectionStuffForSlideHotspots(step, slideId, vform, slides, steps, addConnectionFunction)

      // mark slide as visited
      if(!this.visitedStack.includes(slideId)) {
        this.visitedStack.push(slideId)
      }

      if(this.targetStack.length) {
        const poppedSlide = this.targetStack.pop();
        this.visitedStack.push(poppedSlide);
        this.traverseNode(vform, slides, steps, poppedSlide, addConnectionFunction)
      }
    },
    checkMissingSlides(slides) {
      // todo: check if all slides are in the visitedStack Array – if not, take the one with the lowest index
      const missingSlides = [];
      for(let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        if(!this.visitedStack.includes(slide.id) && !this.targetStack.includes(slide.id)) {
          missingSlides.push(slide.id);
        }
      }
      if(missingSlides.length) {
        console.warn('The following slides were not visited: ', missingSlides);
      }
      return missingSlides;
    },
    /**
     * This method handles the connection stuff for a step, it will get all connection items and push them to the connections array
     * @param {Object} step - the step object
     * @param {String} slideId - the id of the current slide
     * @param {Object} vform - the vform object
     * @param {Array} slides - the array of slides
     * @param {Array} steps - the array of steps
     * @param {Function} addConnectionFunction - a function to add a connection, if provided
     * This function will return an array of target slides that were added
     * */
    handleConnectionStuffForStep(step, slideId, vform, slides, steps, addConnectionFunction = null) {
      let connectionItems = this.getConnectionItems(step, slideId, vform.global, slides, steps);
      for(let i = 0; i < connectionItems.length; i++) {
        const {sourceSlide, targetSlide, label, elementId} = connectionItems[i];
        let computedSourceSlide = sourceSlide ? sourceSlide : slideId;
        if(targetSlide) {
          if(addConnectionFunction) {
            addConnectionFunction(computedSourceSlide, targetSlide, label, elementId, step.uuid);
          }
          // is this duplicate? might be that where handleConnectionStuffForStep is called, after that the checkAndPushConnection is called again
          this.checkAndPushConnection(computedSourceSlide, targetSlide);
        }
      }
    },
    handleConnectionStuffForSlideHotspots(step, slideId, vform, slides, steps, addConnectionFunction = null) {
      let connectionItems = this.getHotspotConnectionItems(step, slideId, vform, slides, steps);
      for(let i = 0; i < connectionItems.length; i++) {
        const {sourceSlide, targetSlide, label, elementId} = connectionItems[i];
        let computedSourceSlide = sourceSlide ? sourceSlide : slideId;
        if(targetSlide) {
          if(addConnectionFunction) {
            addConnectionFunction(computedSourceSlide, targetSlide, label, elementId, step.uuid);
          }
          // is this duplicate? might be that where handleConnectionStuffForStep is called, after that the checkAndPushConnection is called again
          this.checkAndPushConnection(computedSourceSlide, targetSlide);
        }
      }

      // there is 1 panel per slide – the content is in the property vform.slidesMeta
      return connectionItems;
    },
    normalizeConnection(connectionItem, steps) {
      let targetSlide = null;
      const {target, targetId, label, elementId} = connectionItem;
      if(target === vformTargetTypes.SLIDE) {
        targetSlide = targetId;
      } else if(target === vformTargetTypes.STEP) {
        const targetStep = this.getStepById(targetId, steps);
        if(!targetStep) {
          targetSlide = null;
        } else {
          targetSlide = targetStep.linkedSlide;
        }
      } else if(target === vformTargetTypes.PROJECT) {
        console.log('cross project connections are not covered')
      } else if(target === vformTargetTypes.NO_TARGET) {
        targetSlide = null;
      }
      return {targetSlide, label, elementId};
    },
    /**
     * Checks if a connection between two slides exists, if not, it will push the connection to the connections array
     * */
    checkAndPushConnection(slideId, targetSlide) {
      if(targetSlide) {
        const exists = this.checkIfConnectionExists(slideId, targetSlide);
        if(!exists) {
          console.log('checking transition returned false, creating connection')
          this.connections.push({
            source: slideId,
            target: targetSlide
          })
          // if the slide has not been visited yet and is also not registered as a target, we push it to the targetStack
          if(!this.targetStack.includes(targetSlide) && !this.visitedStack.includes(targetSlide)) {
            this.targetStack.push(targetSlide);
          }
        }
      }
    },
    /**
     * if allowOnlyOneWay is set to true, it will check both ways and only return true if neither a -> b nor b -> a is available
     * */
    checkIfConnectionExists(sourceId, targetId, checkBothWays = true) {
      if(sourceId === targetId) {
        return true;
      } else if(!this.connections.length) {
        return false;
      } else {
        return this.connections.filter(item => {
          return (item.source === sourceId && item.target === targetId) || (checkBothWays && item.target === sourceId && item.source === targetId)
        }).length > 0;
      }
    },
    /**
     * Get all Panels for a certain step
     * */
    getStepPanels(step, globalPanels) {
      const globalStepPanels = [];
      for(let i = 0; i < globalPanels.length; i++) {
        const {globalVisibleOnSteps} = globalPanels[i];
        if (globalVisibleOnSteps) {
          if (globalVisibleOnSteps.find((x) => x === step.uuid)) {
            globalStepPanels.push(globalPanels[i]);
          }
        }
      }

      return [...step.panels, ...globalStepPanels];
    },
    dealWithElements(elements, slideId, slides, steps) {
      let connections = [];
      if(elements.length) {
        elements = this.filterByTargetElements(elements);
        for(let i = 0; i < elements.length; i++) {
          const element = elements[i];
          const targets = this.getTargetsForElement(element, slideId, slides, steps);
          if(targets && targets.length) {
            connections.push(...targets)
          } else {
            const label = this.getLabel(element, 'Unknown');
            connections.push({
              targetId: null,
              target: vformTargetTypes.NO_TARGET,
              label: label ? label : element.formElementTypeString
            })
          }
        }
      }
      connections = connections.filter(item => {return !!item });
      connections = connections.map(item => this.normalizeConnection(item, steps));
      return connections;
    },
    getHotspotConnectionItems(step, slideId, vform, slides, steps) {
      let connections = [];
      const {slidesMeta} = vform;
      if(!slidesMeta) {
        console.warn('No slidesMeta found for slide ' + slideId);
        return connections;
      }
      const index = slidesMeta.findIndex(item => item.uuid === slideId);
      if(index !== -1) {
        const slideMeta = slidesMeta[index];
        const {hotspots} = slideMeta;
        if(!hotspots) {
          console.warn('No hotspots found for slide ' + slideId);
          return connections;
        }
        for(let i = 0; i < hotspots.length; i++) {
          const hotspot = hotspots[i];
          const {elements} = hotspot;
          const tmpConnections = this.dealWithElements(elements, slideId, slides, steps);
          connections.push(...tmpConnections);

          let target = this.getTargetByElement(hotspot, slideId, slides, steps);
          if(target) {
            target = this.normalizeConnection(target, steps);
            connections.push(target)
          }
        }
      }
      return connections;
    },
    /**
     * The following items can connect to another place – they will be mapped into standardized objects
     * - Button
     * - Login
     * - Logout
     * - QRCode
     * - Radiobuttons
     *
     * Their sources can be:
     * - vform.step.elements
     * - vform.globals.elements (IF a certain panel is visible on this step)
     *
     * They can either point to a source inside this project or to another project
     *
     * @returns
     * [{
     *   targetId: "targetSlideOrStep",
     *   target: "Slide" // can be step, slide, project or notarget
     *   label
     * }]
     *
     * */
    getConnectionItems(step, slideId, globals, slides, steps) {
      let connections = [];
      const panels = this.getStepPanels(step, globals.panels ? globals.panels : []);
      let elements = [];
      for(let i = 0; i < panels.length; i++) {
        const {uuid, hideBottomBar} = panels[i];
        let els = this.getElementsByPanel(uuid, globals, step, i === 0);

        // this excludes items added in the hidden bottom bar
        if(hideBottomBar) {
          els = els.filter(item => {
            return !item.bottomDropZone
          })
        }
        elements.push(...els);
      }
      const tmpConnections = this.dealWithElements(elements, slideId, slides, steps);
      connections.push(...tmpConnections);
      if(step.autoPlay) {
        // if autoplay is enabled, we need all transitions between slides belonging to this step
        const slideIds = this.getSlidesBelongingToAStep(slides, steps, step.uuid);
        let previousSlideId = slideId;
        for(let i = 0; i < slideIds.length; i++) {
          const targetId = slideIds[i];
          if(targetId === slideId) {
            continue; // skip self
          }
          connections.push({
            sourceSlide: previousSlideId,
            targetSlide: targetId,
            target: vformTargetTypes.SLIDE,
            label: 'AutoPlay'
          });
          if(step.loop && (slideId === slideIds[slideIds.length - 1])) {
            // if loop is enabled, we need to connect the last slide to the first one
            const firstSlideId = slideIds[0];
            connections.push({
              sourceSlide: slideId,
              targetSlide: firstSlideId,
              target: vformTargetTypes.SLIDE,
              label: 'AutoPlay-' + step.uuid
            });
          }
          previousSlideId = targetId;
        }
      }
      return connections;
    },
    // a step contains multiple slides, is only linked to the first one and all slides belong to this one until a new slide comes up with another linked step
    getSlidesBelongingToAStep(slides, steps, stepId) {
      let result = [];
      const stepIndex = steps.findIndex(item => item.uuid === stepId);
      const step = steps[stepIndex];
      const firstStepSlide = step.linkedSlide;
      if(!firstStepSlide) {
        return result; // no slides linked to this step
      }
      const slideIndex = slides.findIndex(item => item.id === firstStepSlide);
      // assuming the steps are ordered, we can just take the next step
      const nextStep = steps[stepIndex + 1];
      const nextSlide = nextStep ? nextStep.linkedSlide : null;
      for(let i = slideIndex; i < slides.length; i++) {
        const slide = slides[i];
        const {id} = slide;
        if(nextSlide && slide.id === nextSlide) {
          break;
        }
        result.push(id);
      }
      return result;
    },
    /**
     * Filter by elements which can trigger going to another step, slide or project
     * */
    filterByTargetElements(elements) {
      if(!elements.length) {
        return [];
      }
      return elements.filter(item => {
        return [
          vFormControls.BUTTON,
          vFormControls.LOGIN,
          vFormControls.LOGOUT,
          vFormControls.QR_CODE,
          vFormControls.RADIOBUTTONS
        ].includes(item.formElementTypeString);
      })
    },
    getTargetsForElement(item, slideId, slides, steps) {
      const {formElementTypeString} = item;
      let targets = [];
      switch(formElementTypeString) {
        case vFormControls.BUTTON: {
          let targets = [];
          if(item.actionMode === vFormButtonActionModes.RULES_BASED && item.rules.length) {
            for(let i = 0; i < item.rules.length; i++) {
              let target = this.getTargetByElement(item.rules[i], slideId, slides, steps);
              if(target) {
                target.elementId = item.uuid;
              }
              targets.push(target);
            }
          } else if(item.actionMode !== vFormButtonActionModes.RULES_BASED) {
            let target = this.getTargetByElement(item, slideId, slides, steps);
            if(target) {
              target.elementId = item.uuid;
            }
            targets.push(target);
          }
          return targets ? targets : null;
        }
        case vFormControls.LOGIN: {
          let target = this.getTargetByElement(item.loginButton, slideId, slides, steps);
          if(target) {
            target.elementId = item.uuid;
          }
          return target ? [target] : null;
        }
        case vFormControls.LOGOUT: {
          item.action = item.selectedAction;
          let target = this.getTargetByElement(item, slideId, slides, steps);
          if(target) {
            target.elementId = item.uuid;
          }
          return target ? [target] : null;
        }
        case vFormControls.QR_CODE: {
          targets = this.getQrCodeTargets(item, slideId, slides, steps);
          targets = targets.map(subitem => {
            subitem.elementId = item.uuid;
            return subitem;
          })
          return targets;
        }
        case vFormControls.RADIOBUTTONS: {
          targets = this.getRadiobuttonTargets(item, slideId, slides, steps);
          targets = targets.map(subitem => {
            subitem.elementId = item.uuid;
            return subitem;
          })
          return targets;
        }
        default:
          return null;
      }
    },
    getRadiobuttonTargets(item, slideId, slides, steps) {
      const {formElementTypeString} = item;
      const targets = [];
      const options = this.getOptions(item);
      for(let i = 0; i < options.length; i++) {
        const option = options[i];
        let target = this.getTargetByElement(option, slideId, slides, steps);
        const label = this.getText(option, 'Unknown');
        const targetLabel = label ? label : formElementTypeString;
        if(target) {
          target.label = targetLabel;
          targets.push(target);
        } else {
          console.log('NO TARGET')
          targets.push({
            targetId: null,
            target: vformTargetTypes.NO_TARGET,
            label: targetLabel
          })
        }
      }
      return targets;
    },
    getQrCodeTargets(item, slideId, slides, steps) {
      const {formElementTypeString} = item;
      const targets = [];
      let codes = item.codes;
      const label = this.getLabel(item, 'Unknown');
      const targetLabel = label ? label : formElementTypeString;
      for(let i = 0; i < codes.length; i++) {
        let code = codes[i];
        code.action = code.selectedAction; // this is an inconsistency here, that's why we need to clear things up
        let target = this.getTargetByElement(code, slideId, slides, steps);
        if(target) {
          target.label = targetLabel + ' ' + (i+1);
          targets.push(target);
        } else {
          targets.push({
            targetId: null,
            target: vformTargetTypes.NO_TARGET,
            label: targetLabel + ' ' + (i+1)
          })
        }
      }
      return targets;
    }
  }
}
</script>